import { type FormEvent, useState } from 'react'
import { encode } from 'uqr'
import { Input } from '@/components/base/input/input'
import { Hinweis } from '@/teile/Hinweis'
import { Knopf } from '@/teile/Knopf'
import { Status } from '@/teile/Status'
import { ApiError } from '../api/client.js'
import { zweiFaktorAbschalten, zweiFaktorAnlegen, zweiFaktorBestaetigen } from '../api/session.js'

/**
 * Zweiter Faktor einrichten (ADR-0004 Nachtrag 6).
 *
 * Pflicht für Selbstverwalter, freiwillig für alle anderen — und nur für den
 * Weg über das Passwort. Wer aus der gekoppelten App kommt, braucht ihn nicht.
 *
 * **Zwei Schritte, mit Absicht.** Nach dem ersten steht das Geheimnis, aber
 * der Faktor ist noch nicht scharf. Erst ein richtiger Code schaltet ihn ein.
 * Wer die falsche App erwischt oder den Code nie einträgt, merkt es hier und
 * nicht bei der nächsten Anmeldung.
 *
 * **Der QR-Code entsteht im Browser** (`uqr`, MIT, ohne eigene Abhängigkeiten).
 * Die `otpauth`-Adresse enthält das Geheimnis und ist ohnehin schon hier; sie
 * noch einmal zum Zeichnen an den Server zu schicken, wäre ein zweiter Weg für
 * dieselbe Sache.
 *
 * Gezeichnet wird aus der **Matrix**, nicht aus fertigem SVG-Text: `encode`
 * liefert ein Feld aus Wahrheitswerten, daraus entstehen React-Elemente. Der
 * Weg über `renderSVG` wäre kürzer, verlangte aber `dangerouslySetInnerHTML` —
 * und eine Stelle, an der Zeichenketten zu Markup werden, will man nicht,
 * auch wenn die Quelle heute vertrauenswürdig ist.
 */

/** Die Matrix als SVG — ein Rechteck je dunklem Feld, nichts sonst. */
function QrBild({ text }: { text: string }) {
  const { size, data } = encode(text)
  const felder: { x: number; y: number }[] = []
  for (const [y, zeile] of data.entries()) {
    for (const [x, dunkel] of zeile.entries()) {
      if (dunkel) felder.push({ x, y })
    }
  }
  return (
    <svg
      viewBox={`-2 -2 ${size + 4} ${size + 4}`}
      className="size-44"
      role="img"
      aria-label="QR-Code für die Authenticator-App"
    >
      <title>QR-Code für die Authenticator-App</title>
      <rect x={-2} y={-2} width={size + 4} height={size + 4} fill="#fff" />
      {felder.map((f) => (
        <rect key={`${f.x}-${f.y}`} x={f.x} y={f.y} width={1} height={1} fill="#000" />
      ))}
    </svg>
  )
}

type Schritt =
  | { name: 'ruht' }
  | { name: 'passwort' }
  | { name: 'einrichten'; uri: string; codes: string[] }
  | { name: 'abschalten' }

export function ZweiterFaktor({
  eingerichtet,
  pflicht,
  vomPartner,
  onGeaendert,
}: {
  eingerichtet: boolean
  /** Pflicht — aus der Rolle oder weil der Partner ihn für alle verlangt. */
  pflicht: boolean
  /** Vom Partner für diesen Kunden gesetzt. Dann lässt er sich nicht abschalten. */
  vomPartner: boolean
  onGeaendert: () => void
}) {
  const [schritt, setSchritt] = useState<Schritt>({ name: 'ruht' })
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  async function anlegen(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    const passwort = String(new FormData(ereignis.currentTarget).get('passwort') ?? '')
    setLaeuft(true)
    setMeldung(null)
    try {
      const { totpURI, backupCodes } = await zweiFaktorAnlegen(passwort)
      setSchritt({ name: 'einrichten', uri: totpURI, codes: backupCodes })
    } catch (fehler) {
      setMeldung(
        fehler instanceof ApiError && fehler.status === 0
          ? fehler.message
          : 'Das Passwort stimmt nicht.',
      )
    } finally {
      setLaeuft(false)
    }
  }

  async function bestaetigen(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    const code = String(new FormData(ereignis.currentTarget).get('code') ?? '').trim()
    setLaeuft(true)
    setMeldung(null)
    try {
      await zweiFaktorBestaetigen(code)
      setSchritt({ name: 'ruht' })
      onGeaendert()
    } catch {
      setMeldung('Der Code stimmt nicht. Bitte den aktuellen aus Ihrer App eingeben.')
    } finally {
      setLaeuft(false)
    }
  }

  async function abschalten(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    const passwort = String(new FormData(ereignis.currentTarget).get('passwort') ?? '')
    setLaeuft(true)
    setMeldung(null)
    try {
      await zweiFaktorAbschalten(passwort)
      setSchritt({ name: 'ruht' })
      onGeaendert()
    } catch {
      setMeldung('Das Passwort stimmt nicht.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline gap-2">
        <h2 className="font-display text-base font-bold text-primary">Zweiter Faktor</h2>
        {eingerichtet ? (
          <Status ton="gut">eingerichtet</Status>
        ) : pflicht ? (
          <Status ton="stoerung">fehlt</Status>
        ) : (
          <Status ton="ruhig">aus</Status>
        )}
      </div>

      {pflicht && !eingerichtet ? (
        <p className="max-w-prose text-sm text-primary">
          Ohne ihn können Sie sich nicht mit Passwort anmelden.
        </p>
      ) : null}

      {meldung ? (
        <Hinweis art="fehler" titel="Nicht geklappt">
          {meldung}
        </Hinweis>
      ) : null}

      {schritt.name === 'ruht' ? (
        <div className="flex items-center gap-3">
          {eingerichtet ? (
            // Verlangt der Partner ihn, gibt es hier nichts abzuschalten. Der
            // Endpunkt weist es ohnehin ab — der fehlende Knopf erspart nur
            // den Versuch.
            vomPartner ? (
              <p className="text-[12.5px] text-tertiary">Abschalten geht nur über Ihren Partner.</p>
            ) : (
              <Knopf gefahr onClick={() => setSchritt({ name: 'abschalten' })}>
                Abschalten
              </Knopf>
            )
          ) : (
            <Knopf stark onClick={() => setSchritt({ name: 'passwort' })}>
              Einrichten
            </Knopf>
          )}
        </div>
      ) : null}

      {schritt.name === 'passwort' || schritt.name === 'abschalten' ? (
        <form
          onSubmit={schritt.name === 'passwort' ? anlegen : abschalten}
          className="flex max-w-sm flex-col gap-3"
        >
          <Input
            isRequired
            name="passwort"
            type="password"
            label="Ihr Passwort"
            autoComplete="current-password"
            size="sm"
          />
          <div className="flex gap-2">
            <Knopf
              stark={schritt.name === 'passwort'}
              gefahr={schritt.name === 'abschalten'}
              type="submit"
              disabled={laeuft}
            >
              {schritt.name === 'passwort' ? 'Weiter' : 'Abschalten'}
            </Knopf>
            <Knopf onClick={() => setSchritt({ name: 'ruht' })} disabled={laeuft}>
              Abbrechen
            </Knopf>
          </div>
        </form>
      ) : null}

      {schritt.name === 'einrichten' ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start gap-5">
            <div className="shrink-0 rounded-xl border border-secondary bg-white p-3">
              <QrBild text={schritt.uri} />
            </div>
            <div className="flex max-w-sm flex-col gap-2">
              <p className="text-sm text-tertiary">Oder den Schlüssel von Hand eintragen:</p>
              <code className="rounded-lg border border-secondary bg-secondary px-3 py-2 font-mono text-[12px] break-all select-all">
                {new URL(schritt.uri).searchParams.get('secret') ?? schritt.uri}
              </code>
            </div>
          </div>

          <div className="max-w-prose rounded-xl border border-achtung bg-achtung-flaeche p-4">
            <p className="text-[13px] font-semibold text-achtung-tief">
              Wiederherstellungscodes — erscheinen nur jetzt
            </p>
            <ul className="mt-2 grid grid-cols-2 gap-1 font-mono text-[12.5px] text-primary sm:grid-cols-3">
              {schritt.codes.map((code) => (
                <li key={code} className="select-all">
                  {code}
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={bestaetigen} className="flex max-w-sm flex-col gap-3">
            <Input
              isRequired
              name="code"
              label="Code aus der App"
              autoComplete="one-time-code"
              inputMode="numeric"
              size="sm"
            />
            <div className="flex gap-2">
              <Knopf stark type="submit" disabled={laeuft}>
                Einschalten
              </Knopf>
              <Knopf onClick={() => setSchritt({ name: 'ruht' })} disabled={laeuft}>
                Abbrechen
              </Knopf>
            </div>
          </form>
        </div>
      ) : null}
    </section>
  )
}
